// src/Pages/Admin/Referrals/commissionTypeConfig.js
import React, { useState } from 'react';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  TableContainer,
  Box,
  Typography,
  Chip,
  Avatar,
  IconButton,
  TextField,
  InputAdornment,
  Tooltip,
  Stack,
  Switch
} from '@mui/material';
import {
  Receipt,
  Campaign as CampaignIcon,
  Work,
  Subscriptions,
  EmojiEvents,
  SwapHoriz,
  Save,
  Close,
  Edit,
  Add,
  Delete
} from '@mui/icons-material';

// =================== COMMISSION TYPE CONFIGURATION ===================
export const COMMISSION_TYPES = [
  { 
    id: 'blitzpay', 
    label: 'BlitzPay', 
    icon: <Receipt />, 
    color: 'primary',
    description: 'Invoice factoring spread'
  },
  { 
    id: 'campaign', 
    label: 'Campaign', 
    icon: <CampaignIcon />, 
    color: 'secondary',
    description: 'Creator cost/spend spread'
  },
  { 
    id: 'project', 
    label: 'Project', 
    icon: <Work />, 
    color: 'info',
    description: 'Base platform fee'
  },
  { 
    id: 'subscription', 
    label: 'Subscription', 
    icon: <Subscriptions />, 
    color: 'success',
    description: 'Platform revenue'
  },
  { 
    id: 'performance_bonus', 
    label: 'Performance Bonus', 
    icon: <EmojiEvents />, 
    color: 'warning',
    description: 'Marketing revenue'
  },
  { 
    id: 'referral', 
    label: 'Referral', 
    icon: <SwapHoriz />, 
    color: 'error',
    description: 'Existing referral commissions'
  }
];

// =================== TYPE-SPECIFIC RATE CONFIGURATION PANEL ===================
export const TypeRateConfigPanel = ({ 
  commissionTypes = COMMISSION_TYPES,
  baseRate,
  typeSpecificRates = {},
  enabledTypes = null,
  onUpdateTypeRate,
  onDeleteTypeRate,
  onToggleType,
  readOnly = false
}) => {
  const [editingType, setEditingType] = useState(null);
  const [tempRate, setTempRate] = useState(null);

  const isTypeEnabled = (typeId) => {
    return enabledTypes === null || enabledTypes.includes(typeId);
  };

  const handleStartEdit = (typeId, currentRate) => {
    setEditingType(typeId);
    setTempRate(currentRate);
  };

  const handleSaveEdit = (typeId) => {
    if (tempRate === baseRate) {
      // Same as base rate, remove override
      onDeleteTypeRate?.(typeId);
    } else {
      onUpdateTypeRate?.(typeId, tempRate);
    }
    setEditingType(null);
    setTempRate(null);
  };

  const handleCancelEdit = () => {
    setEditingType(null);
    setTempRate(null);
  };

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell width="40%">Commission Type</TableCell>
            <TableCell width="15%">Enabled</TableCell>
            <TableCell width="20%">Rate</TableCell>
            <TableCell width="15%">Override</TableCell>
            {!readOnly && <TableCell width="10%" align="right">Actions</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {commissionTypes.map((type) => {
            const typeEnabled = isTypeEnabled(type.id);
            const override = typeSpecificRates[type.id];
            const effectiveRate = override !== undefined ? override : baseRate;
            const isEditing = editingType === type.id;

            return (
              <TableRow 
                key={type.id} 
                sx={{ 
                  opacity: typeEnabled ? 1 : 0.5,
                  bgcolor: override !== undefined ? 'warning.50' : 'inherit'
                }}
              >
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <Avatar 
                      sx={{ 
                        width: 36, 
                        height: 36, 
                        bgcolor: `${type.color}.light`,
                        color: `${type.color}.main`
                      }}
                    >
                      {type.icon}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {type.label}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {type.description}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>

                <TableCell>
                  <Switch
                    checked={typeEnabled}
                    onChange={() => onToggleType?.(type.id)}
                    size="small"
                    disabled={readOnly}
                    color={type.color}
                  />
                </TableCell>

                <TableCell>
                  {isEditing ? (
                    <TextField
                      size="small"
                      type="number"
                      value={tempRate}
                      onChange={(e) => setTempRate(Math.max(0, Math.min(10000, parseInt(e.target.value) || 0)))}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">bps</InputAdornment>
                      }}
                      sx={{ width: 120 }}
                      autoFocus
                    />
                  ) : typeEnabled ? (
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {(effectiveRate / 100).toFixed(2)}%
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        ({effectiveRate} bps)
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="caption" color="textSecondary">
                      Disabled
                    </Typography>
                  )}
                </TableCell>

                <TableCell>
                  {typeEnabled && (
                    override !== undefined ? (
                      <Chip 
                        label="Custom" 
                        size="small" 
                        color="warning" 
                        variant="outlined"
                      />
                    ) : (
                      <Chip 
                        label="Base" 
                        size="small" 
                        variant="outlined"
                      />
                    )
                  )}
                </TableCell>

                {!readOnly && (
                  <TableCell align="right">
                    {isEditing ? (
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <IconButton size="small" color="primary" onClick={() => handleSaveEdit(type.id)}>
                          <Save fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={handleCancelEdit}>
                          <Close fontSize="small" />
                        </IconButton>
                      </Stack>
                    ) : typeEnabled && (
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title={override !== undefined ? "Edit Override" : "Add Override"}>
                          <IconButton 
                            size="small" 
                            color="primary" 
                            onClick={() => handleStartEdit(type.id, effectiveRate)}
                          >
                            {override !== undefined ? <Edit fontSize="small" /> : <Add fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                        {override !== undefined && (
                          <Tooltip title="Remove Override">
                            <IconButton 
                              size="small" 
                              color="error" 
                              onClick={() => {
                                if (window.confirm('Remove this override? It will revert to the base rate.')) {
                                  onDeleteTypeRate?.(type.id);
                                }
                              }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    )}
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};